import type { DeepLinkPluginElement } from '../domains/reporter/RichTextEditor/plugins/deepLink/types';

import { useToasterDispatch } from '../common/ui/Toaster/Toaster';
import {
  editableStudyDeepLinkState,
  templateInsertionCompletionState,
} from '../domains/reporter/Reporter/state';
import {
  getDeepLinkKey,
  getExistingBookmarkWithStudyDeepLinks,
  populateComparisonStudyNodes,
  removeAllComparisonStudyNodes,
  type PopulateComparisonStudyNodesOptions,
} from '../domains/reporter/RichTextEditor/plugins/deepLink/utils';
import { useTemplateBookmarkTextMap } from '../domains/reporter/RichTextEditor/plugins/inlineBookmark/useTemplateBookmarkTextMap';
import {
  getComparisonHeading,
  getNextHeadingOrHeadingLikeNode,
} from 'domains/reporter/RichTextEditor/plugins/heading/utils';
import { useStudies } from '../domains/viewer/Viewer/StudyLoader/useStudies';
import {
  useReviewedStudiesByDate,
  type ReviewedStudyByDate,
} from '../domains/viewer/ViewportDre/modules/imaging/useTrackViewedSlices';
import { NameSpaces, sendCrossTabEvent } from '../modules/EventsManager/eventsManager';
import { useCaseSync } from './useCaseSync';
import { MAX_VIEWER_PRIOR_STUDIES } from 'domains/reporter/Reporter/LinkCases/constants';
import { useReportLockedState } from 'domains/reporter/Reporter/hooks/useReportLockedState';
import { useCurrentComparativeStudies } from './useCurrentCase';
import { useHeadingKeywords } from './useHeadingKeywords';
import useStudyIds from './useStudyIds';
import useWorklistId from './useWorklistId';
import { logger } from 'modules/logger';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Editor, Range, Transforms } from 'slate';
import { Key, ReactEditor, useSlate, useSlateSelection } from 'slate-react';
import { FF, useFeatureFlagEnabled } from 'modules/feature-flags';
import { DeepLinkVariants } from '../domains/reporter/RichTextEditor/plugins/deepLink/constants';
import type { SetterOrUpdater } from 'recoil';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { StudyDeepLinkElement } from 'domains/reporter/RichTextEditor/slate-custom-types';
import { WithApolloContext } from 'modules/Apollo/types';
import type { TEditableState } from '../domains/reporter/Reporter/state';

export const CASE_REVIEWED_STUDIES_CACHE_KEY_PREFIX = 'caseReviewedStudiesBackup:';

type DeepLinkReturnType = {
  handleDeepLinkClick: (element: DeepLinkPluginElement | null, editor: Editor | null) => void;
  handleEditDeepLinkClick: (element: DeepLinkPluginElement | null, editor: Editor | null) => void;
  getIsEditingStudyNode: (key: Key) => boolean;
};

type DeepLinkHandlers = DeepLinkReturnType & {
  setEditableStudyDeepLinks: SetterOrUpdater<Map<Key, TEditableState>>;
};

// Shared deep-link interaction logic that does not rely on Slate context.
// Use this when you need handlers outside of <Slate> (e.g. plugin initialization).
export const useDeepLinkStudyHandlers = ({
  isAutoFillComparisonEnabled = false,
  context,
}: {
  isAutoFillComparisonEnabled?: boolean;
} & WithApolloContext): DeepLinkHandlers => {
  const setEditableStudyDeepLinks = useSetRecoilState(editableStudyDeepLinkState);
  const syncCase = useCaseSync({ context });
  const [currentComparativeStudies, setCurrentComparativeStudies] = useCurrentComparativeStudies({
    context,
  });
  const studyIds = useStudyIds({ context });
  const { studies } = useStudies({ context });
  const currentStudySmid = studies[0]?.smid;
  const currentCaseId = useWorklistId();
  const { enqueueToast } = useToasterDispatch();
  const [imageBookmarkFlag] = useFeatureFlagEnabled(FF.IMAGE_BOOKMARK);

  // On click comparison node load study in viewer
  const loadLazyStudy = useCallback(
    (smid: string) => {
      if (currentCaseId == null) return;

      syncCase(currentCaseId, currentComparativeStudies.concat(smid));

      if (smid) {
        setCurrentComparativeStudies({
          caseSmid: currentCaseId,
          studySmids: currentComparativeStudies.concat(smid),
        });
      }
    },
    [syncCase, currentCaseId, currentComparativeStudies, setCurrentComparativeStudies]
  );

  // Single click on deep link scrolls to study in viewer and opens menu
  const handleDeepLinkClick = useCallback(
    (element: DeepLinkPluginElement | null, editor: Editor | null) => {
      if (element == null) return;
      const { children: _children, type, ...node } = element;
      const isImageBookmark =
        (imageBookmarkFlag || element.variant === DeepLinkVariants.POINT) &&
        element.variant !== DeepLinkVariants.STUDY;
      const isAutoFillComparison =
        isAutoFillComparisonEnabled && element.variant === DeepLinkVariants.STUDY;

      if (isImageBookmark) {
        sendCrossTabEvent(NameSpaces.DEEP_LINK, {
          type: 'HANDLE_IMAGE',
          payload: node,
        });
      } else if (isAutoFillComparison) {
        if (editor == null || element == null) return;

        const clickedStudySmid = (node.context as StudyDeepLinkElement['context']).studySmid ?? '';
        if (![currentStudySmid, ...studyIds].includes(clickedStudySmid)) {
          if (studyIds.length >= MAX_VIEWER_PRIOR_STUDIES) {
            enqueueToast(
              `You can view a maximum of ${MAX_VIEWER_PRIOR_STUDIES} prior studies simultaneously. Please remove a study from the Viewer to proceed.`,
              { severity: 'error' }
            );
          } else {
            loadLazyStudy(clickedStudySmid);
          }
        }
        sendCrossTabEvent(NameSpaces.DEEP_LINK, {
          type: 'HANDLE_STUDY',
          payload: node,
        });
        logger.info('[AutoFillComparison] Event HANDLE_STUDY sent to viewer', node);

        // Move selection to the end of the deep link element after click
        ReactEditor.focus(editor);
        try {
          const path = ReactEditor.findPath(editor, element);
          const after = Editor.after(editor, path);
          if (after == null) return;
          Transforms.select(editor, after);
        } catch (e: any) {
          logger.error('Error selecting after of the deep link element', e);
        }

        // selection has delayed effect, so we need to wait for it to take effect
        setTimeout(() => {
          const deepLinkKey = getDeepLinkKey(editor, element);
          // Only have one menu open after click
          setEditableStudyDeepLinks((previousState) => {
            const previousMap = previousState instanceof Map ? previousState : new Map();
            const updateState = new Map(previousMap);
            // Iterate through all entries in updateState
            previousState.forEach((value, key) => {
              if (key !== deepLinkKey) {
                updateState.set(key, 'readOnly');
              }
            });
            updateState.set(deepLinkKey, 'menuOpen');

            logger.info('[AutoFillComparison] Deep Link Clicked - Menu Open', element);

            return updateState;
          });
        }, 0);
      }
    },
    [
      imageBookmarkFlag,
      isAutoFillComparisonEnabled,
      currentStudySmid,
      studyIds,
      enqueueToast,
      loadLazyStudy,
      setEditableStudyDeepLinks,
    ]
  );

  // Double click on deep link or when edit button is clicked in menu
  const handleEditDeepLinkClick = useCallback(
    // @ts-expect-error [EN-10263] - TS7006 - Parameter 'element' implicitly has an 'any' type. | TS7006 - Parameter 'editor' implicitly has an 'any' type.
    (element, editor) => {
      if (editor == null || element == null || !isAutoFillComparisonEnabled) return;
      const { children: _children, type, ...node } = element;
      logger.info('[AutoFillComparison] Deep Link Clicked - Editing', node);
      // Only have one menu open after click
      setEditableStudyDeepLinks((previousState) => {
        const deepLinkKey = getDeepLinkKey(editor, element);
        const previousMap = previousState instanceof Map ? previousState : new Map();
        const updateState = new Map(previousMap);
        // Iterate through all entries in updateState
        previousState.forEach((value, key) => {
          if (key !== deepLinkKey) {
            updateState.set(key, 'readOnly');
          }
        });
        updateState.set(deepLinkKey, 'editing');
        return updateState;
      });

      // Move selection to the end of the deep link element to start editing
      requestAnimationFrame(() => {
        ReactEditor.focus(editor);
        try {
          const path = ReactEditor.findPath(editor, element);
          const end = Editor.end(editor, path);
          if (end == null) return;
          Transforms.select(editor, end);
        } catch (e: any) {
          logger.info('[AutoFillComparison] Error selecting the end of the deep link element', e);
        }
      });
    },
    [isAutoFillComparisonEnabled, setEditableStudyDeepLinks]
  );

  const getIsEditingStudyNode = useCallback(
    (key: Key) => {
      let isEditing = false;
      // enclose in set function to get the latest state
      setEditableStudyDeepLinks((previousState) => {
        isEditing = previousState.get(key) === 'editing';
        return previousState;
      });
      return isEditing;
    },
    [setEditableStudyDeepLinks]
  );

  return {
    handleDeepLinkClick,
    handleEditDeepLinkClick,
    getIsEditingStudyNode,
    setEditableStudyDeepLinks,
  };
};

// Full deep-link hook for use inside <Slate>, including editor side effects and population logic.
export const useDeepLinkStudy = ({
  isAutoFillComparisonEnabled = false,
  context,
}: {
  isAutoFillComparisonEnabled?: boolean;
} & WithApolloContext): DeepLinkReturnType => {
  const editor = useSlate();
  const selection = useSlateSelection();
  const headingKeywords = useHeadingKeywords({ context });
  const reviewedStudiesByDate = useReviewedStudiesByDate({ context });
  const currentCaseId = useWorklistId();
  const studyIds = useStudyIds({ context });
  const { studies, mainStudiesIds } = useStudies({ context });
  const currentStudySmid = studies[0]?.smid;
  const linkedSmids = useMemo(() => new Set(mainStudiesIds), [mainStudiesIds]);
  const templateInsertionCompletion = useRecoilValue(templateInsertionCompletionState);
  const { templateBookmarkTextMap } = useTemplateBookmarkTextMap();
  const { isClaimedByMe } = useReportLockedState({ context });

  const {
    handleDeepLinkClick,
    handleEditDeepLinkClick,
    getIsEditingStudyNode,
    setEditableStudyDeepLinks,
  } = useDeepLinkStudyHandlers({
    isAutoFillComparisonEnabled,
    context,
  });

  // Track which template insertions have already triggered comparison population
  // This prevents duplicate population within the same insertion event
  const populatedTemplatesRef = useRef<Set<string>>(new Set());

  // Track the previous claim state and case id to determine if the case has been reclaimed
  const previousClaimStateRef = useRef<Nullable<boolean>>(undefined);
  const previousCaseIdRef = useRef<Nullable<string>>(currentCaseId);
  // Track the last reviewed studies to populate comparison nodes when the case is reclaimed
  const lastReviewedStudiesRef = useRef<Map<string, ReviewedStudyByDate> | null>(null);

  // Get the cached reviewed studies from local storage for reclaiming a case
  const getCachedReviewedStudies = useCallback(() => {
    if (typeof window === 'undefined' || currentCaseId == null) return null;

    try {
      const serialized = localStorage.getItem(
        `${CASE_REVIEWED_STUDIES_CACHE_KEY_PREFIX}${currentCaseId}`
      );
      if (serialized == null) return null;
      const parsed = JSON.parse(serialized);
      return Array.isArray(parsed) ? new Map<string, ReviewedStudyByDate>(parsed) : null;
    } catch (error: any) {
      logger.warn(
        '[AutoFillComparison] Failed to read cached reviewed studies from local storage',
        error
      );
      return null;
    }
  }, [currentCaseId]);

  const handlePopulateComparisonStudyNodes = useCallback(
    (
      overrideReviewedStudies?: Nullable<Map<string, ReviewedStudyByDate>>,
      options?: PopulateComparisonStudyNodesOptions
    ) => {
      if (!isAutoFillComparisonEnabled || editor == null) return;

      const studiesFromSource = overrideReviewedStudies ?? reviewedStudiesByDate;

      let studiesToPopulate =
        studiesFromSource != null && studiesFromSource.size > 0
          ? studiesFromSource
          : lastReviewedStudiesRef.current;

      let usedLocalStorageFallback = false;

      if (studiesToPopulate == null || studiesToPopulate.size === 0) {
        const cachedReviewedStudies = getCachedReviewedStudies();

        if (cachedReviewedStudies != null && cachedReviewedStudies.size > 0) {
          studiesToPopulate = cachedReviewedStudies;
          usedLocalStorageFallback = true;
          logger.info(
            '[AutoFillComparison] Using cached reviewed studies from local storage for repopulation',
            cachedReviewedStudies
          );
        }
      }

      if (studiesToPopulate == null || studiesToPopulate.size === 0) {
        logger.info(
          '[AutoFillComparison] No reviewed studies available to populate comparison nodes; removing any existing comparison nodes'
        );
        removeAllComparisonStudyNodes(editor, headingKeywords.comparison);
        return;
      }

      logger.info('[AutoFillComparison] Reviewed Study Smids from Viewer.', studiesToPopulate);

      populateComparisonStudyNodes(
        editor,
        studiesToPopulate,
        headingKeywords.comparison,
        currentStudySmid,
        studyIds,
        { ...options, linkedSmids, templateBookmarkTextMap }
      );

      // Update the last reviewed studies to use for reclaimed
      lastReviewedStudiesRef.current = new Map(studiesToPopulate);

      if (usedLocalStorageFallback && typeof window !== 'undefined' && currentCaseId != null) {
        localStorage.removeItem(`${CASE_REVIEWED_STUDIES_CACHE_KEY_PREFIX}${currentCaseId}`);
      }
    },
    [
      editor,
      headingKeywords.comparison,
      isAutoFillComparisonEnabled,
      reviewedStudiesByDate,
      currentStudySmid,
      studyIds,
      linkedSmids,
      getCachedReviewedStudies,
      currentCaseId,
      templateBookmarkTextMap,
    ]
  );

  // Populate comparison study nodes when template is ready
  useEffect(() => {
    if (!isAutoFillComparisonEnabled || editor == null) return;

    const { sequence, templateId, source } = templateInsertionCompletion;
    if (sequence === 0 || templateId == null) return;

    const trackingKey = `${templateId}-${source ?? 'unknown'}-${sequence}`;

    if (populatedTemplatesRef.current.has(trackingKey)) {
      logger.info('[AutoFillComparison] Already populated for this tracking key', { trackingKey });
      return;
    }

    logger.info('[AutoFillComparison] Template ready, populating comparison study nodes', {
      templateId,
      source,
      trackingKey,
    });

    populatedTemplatesRef.current.add(trackingKey);
    handlePopulateComparisonStudyNodes(undefined, { replaceExistingBookmarkContent: true });
  }, [
    isAutoFillComparisonEnabled,
    editor,
    templateInsertionCompletion,
    handlePopulateComparisonStudyNodes,
  ]);

  // Also trigger when reviewed studies change
  useEffect(
    () => {
      if (!isAutoFillComparisonEnabled || editor == null || reviewedStudiesByDate == null) return;

      logger.info('[AutoFillComparison] Reviewed Study Smids from Viewer.', reviewedStudiesByDate);

      handlePopulateComparisonStudyNodes();
    },
    // trigger every time the reviewed studies or linked study membership changes
    [
      handlePopulateComparisonStudyNodes,
      editor,
      isAutoFillComparisonEnabled,
      reviewedStudiesByDate,
      mainStudiesIds,
    ]
  );

  // Update the last reviewed studies to use for future repopulation when the case is reclaimed
  useEffect(() => {
    if (reviewedStudiesByDate == null || reviewedStudiesByDate.size === 0) return;
    lastReviewedStudiesRef.current = new Map(reviewedStudiesByDate);
  }, [reviewedStudiesByDate]);

  // Repopulate comparison nodes when the case is reclaimed
  useEffect(() => {
    const previousIsClaimedByMe = previousClaimStateRef.current;
    const previousCaseId = previousCaseIdRef.current;
    const hasCaseChanged = previousCaseId !== currentCaseId;
    const hasLostClaim = previousIsClaimedByMe && !isClaimedByMe;
    const hasRegainedClaim = previousIsClaimedByMe === false && isClaimedByMe;

    if (hasCaseChanged || hasLostClaim) {
      populatedTemplatesRef.current.clear();
      if (hasCaseChanged) {
        lastReviewedStudiesRef.current = null;
      }
    }

    previousClaimStateRef.current = isClaimedByMe ?? null;
    previousCaseIdRef.current = currentCaseId ?? null;

    if (!isAutoFillComparisonEnabled || !hasRegainedClaim) {
      return;
    }

    const studiesToReuse =
      reviewedStudiesByDate != null && reviewedStudiesByDate.size > 0
        ? reviewedStudiesByDate
        : lastReviewedStudiesRef.current;

    logger.info('[AutoFillComparison] Claim regained, repopulating comparison study nodes', {
      worklistItemSmid: currentCaseId,
    });

    handlePopulateComparisonStudyNodes(studiesToReuse, { allowStaleComparatives: true });
  }, [
    currentCaseId,
    handlePopulateComparisonStudyNodes,
    isAutoFillComparisonEnabled,
    isClaimedByMe,
    reviewedStudiesByDate,
  ]);

  // Handle outside click to close menu
  useEffect(() => {
    if (editor == null || selection == null || !isAutoFillComparisonEnabled) return;

    const comparisonHeading = getComparisonHeading(editor, headingKeywords.comparison);
    if (comparisonHeading == null) return;

    const nextHeading = getNextHeadingOrHeadingLikeNode({ editor, location: comparisonHeading[1] });
    if (nextHeading == null) return;

    const node = getExistingBookmarkWithStudyDeepLinks(
      editor,
      comparisonHeading[1],
      nextHeading[1]
    );
    if (node == null) return;

    const isInsideComparison = Range.includes(selection, node[1]);
    // Reset all editable study deep links if the selection is outside of the comparison field
    if (!isInsideComparison) {
      setEditableStudyDeepLinks(new Map());
    }
  }, [
    editor,
    headingKeywords.comparison,
    isAutoFillComparisonEnabled,
    selection,
    setEditableStudyDeepLinks,
  ]);

  return {
    handleDeepLinkClick,
    handleEditDeepLinkClick,
    getIsEditingStudyNode,
  };
};
